import type { CashFlowRow } from "@/hooks/finance/cash-flow/useCashFlow";
import { REPORT_SECTIONS, type MatchRule } from "./mapping";

const norm = (s: string) => s.trim().toLowerCase();

/**
 * Build an index of the operating expense rows returned by
 * useCashFlowStatementDetails, keyed by normalised group label.
 */
function indexOperatingGroups(rows: CashFlowRow[]) {
  const byGroup = new Map<
    string,
    { groupRow: CashFlowRow; positions: Map<string, CashFlowRow> }
  >();

  for (const r of rows) {
    if (!r.key.startsWith("op-exp-")) continue;
    // Group label is wrapped in parens by useCashFlow: "(Admin Cost)"
    const raw = r.label.replace(/^\(/, "").replace(/\)$/, "");
    const positions = new Map<string, CashFlowRow>();
    for (const child of r.children ?? []) {
      positions.set(norm(child.label), child);
    }
    byGroup.set(norm(raw), { groupRow: r, positions });
  }

  return byGroup;
}

function emptyValues(periodKeys: string[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const k of periodKeys) m.set(k, 0);
  return m;
}

function addInto(target: Map<string, number>, src?: Map<string, number>) {
  if (!src) return;
  for (const [k, v] of src.entries()) {
    target.set(k, (target.get(k) ?? 0) + (v ?? 0));
  }
}

function subtractInto(target: Map<string, number>, src?: Map<string, number>) {
  if (!src) return;
  for (const [k, v] of src.entries()) {
    target.set(k, (target.get(k) ?? 0) - (v ?? 0));
  }
}

function aggregateMatches(
  matches: MatchRule[],
  index: ReturnType<typeof indexOperatingGroups>,
  periodKeys: string[],
  consumed: Set<string>
): Map<string, number> {
  const out = emptyValues(periodKeys);

  for (const m of matches) {
    const entry = index.get(norm(m.group));
    if (!entry) continue;

    if (!("positions" in m) && !("excludePositions" in m)) {
      // whole group
      addInto(out, entry.groupRow.values);
      // mark every position + the group itself as consumed
      consumed.add(`group:${norm(m.group)}`);
      for (const p of entry.positions.keys()) {
        consumed.add(`pos:${norm(m.group)}:${p}`);
      }
      continue;
    }

    if ("positions" in m) {
      for (const posLabel of m.positions) {
        const pos = entry.positions.get(norm(posLabel));
        if (pos) {
          addInto(out, pos.values);
          consumed.add(`pos:${norm(m.group)}:${norm(posLabel)}`);
        }
      }
      continue;
    }

    if ("excludePositions" in m) {
      addInto(out, entry.groupRow.values);
      consumed.add(`group:${norm(m.group)}`);
      // consume every position in this group; excluded ones will be
      // subtracted here and re-attributed via their own explicit line.
      for (const [pKey] of entry.positions.entries()) {
        consumed.add(`pos:${norm(m.group)}:${pKey}`);
      }
      for (const posLabel of m.excludePositions) {
        const pos = entry.positions.get(norm(posLabel));
        if (pos) subtractInto(out, pos.values);
      }
    }
  }

  return out;
}

export type FinancialReportBuild = {
  rows: CashFlowRow[];
  unmappedRows: CashFlowRow[];
};

/**
 * Transform the raw cash-flow rows into the "Monthly CF-ETB" style layout.
 * Preserves the CashFlowRow shape so the existing StatementTable renders it.
 */
export function buildFinancialReportRows(
  cashFlowRows: CashFlowRow[],
  periodKeys: string[]
): FinancialReportBuild {
  const opIndex = indexOperatingGroups(cashFlowRows);
  const consumed = new Set<string>();

  // Passthrough helpers to find totals/openings already computed upstream
  const findByKey = (k: string) => cashFlowRows.find((r) => r.key === k);
  const opening = findByKey("opening");
  const closing = findByKey("closing");
  const totalSales = findByKey("total-sales");
  const chipchipSales = findByKey("chipchip-sales");
  const chipchipFees = findByKey("chipchip-fees");
  const b2bSales = findByKey("b2b-sales");

  const rows: CashFlowRow[] = [];

  if (opening) {
    rows.push({
      ...opening,
      key: "fr-opening",
      label: "Cash Opening",
    });
  }

  // Inflows section
  rows.push({
    key: "fr-inflow-header",
    label: "TOTAL OPERATING INFLOWS / REVENUES",
    isHeader: true,
    values: emptyValues(periodKeys),
  });
  if (chipchipSales)
    rows.push({ ...chipchipSales, key: "fr-chipchip-sales", indent: 1 });
  if (chipchipFees)
    rows.push({ ...chipchipFees, key: "fr-chipchip-fees", indent: 1 });
  if (b2bSales) rows.push({ ...b2bSales, key: "fr-b2b-sales", indent: 1 });
  if (totalSales) {
    rows.push({
      ...totalSales,
      key: "fr-total-sales",
      label: "Total Sales Inflow",
      isTotal: true,
      indent: 0,
    });
  }

  // Operating inflows (grants / cost reimbursements / refunds) — surfaced from
  // the raw statement's op-in-* rows as gross operating income under the same
  // inflows header, and folded into Net Operating below.
  const operatingInflowRows = cashFlowRows.filter((r) => r.key.startsWith("op-in-"));
  const operatingInflowTotal = emptyValues(periodKeys);
  for (const r of operatingInflowRows) {
    // Summary view: keep the income line, drop the per-record drill-down.
    rows.push({ ...r, key: `fr-${r.key}`, indent: 1, children: undefined });
    addInto(operatingInflowTotal, r.values);
  }

  // Outflow sections
  const totalOutflow = emptyValues(periodKeys);

  for (const section of REPORT_SECTIONS) {
    rows.push({
      key: `fr-sec-${section.title}`,
      label: section.title.toUpperCase(),
      isHeader: true,
      values: emptyValues(periodKeys),
    });

    const sectionSubtotal = emptyValues(periodKeys);

    for (const line of section.lines) {
      const values = aggregateMatches(line.matches, opIndex, periodKeys, consumed);
      addInto(sectionSubtotal, values);
      rows.push({
        key: `fr-line-${section.title}-${line.label}`,
        label: line.label,
        indent: 1,
        values,
      });
    }

    addInto(totalOutflow, sectionSubtotal);
    rows.push({
      key: `fr-sub-${section.title}`,
      label: section.subtotalLabel,
      isTotal: true,
      indent: 0,
      values: sectionSubtotal,
    });
  }

  // Unmapped operating cost positions (diagnostic)
  const unmappedRows: CashFlowRow[] = [];
  const unmappedTotal = emptyValues(periodKeys);
  for (const [gKey, entry] of opIndex.entries()) {
    if (consumed.has(`group:${gKey}`)) {
      // whole-group already consumed — still surface positions that weren't
      // explicitly consumed via positions/excludePositions AND weren't
      // covered by whole-group consumption (they are, so nothing to add).
      continue;
    }
    if (entry.positions.size === 0) {
      // Group with no children (single position) and not consumed
      unmappedRows.push({
        key: `fr-unmapped-g-${gKey}`,
        label: `(${entry.groupRow.label.replace(/^\(|\)$/g, "")})`,
        indent: 1,
        values: entry.groupRow.values,
      });
      addInto(unmappedTotal, entry.groupRow.values);
      continue;
    }
    for (const [pKey, pos] of entry.positions.entries()) {
      if (consumed.has(`pos:${gKey}:${pKey}`)) continue;
      unmappedRows.push({
        key: `fr-unmapped-${gKey}-${pKey}`,
        label: `${entry.groupRow.label.replace(/^\(|\)$/g, "")} › ${pos.label}`,
        indent: 1,
        values: pos.values,
      });
      addInto(unmappedTotal, pos.values);
    }
  }

  // Grand totals
  const netOperating = emptyValues(periodKeys);
  if (totalSales) addInto(netOperating, totalSales.values);
  // operating income (grants / reimbursements) adds to net operating
  addInto(netOperating, operatingInflowTotal);
  subtractInto(netOperating, totalOutflow);
  // include unmapped in Total Operating Outflows so the number stays reconciled
  subtractInto(netOperating, unmappedTotal);

  const totalOutflowWithUnmapped = emptyValues(periodKeys);
  addInto(totalOutflowWithUnmapped, totalOutflow);
  addInto(totalOutflowWithUnmapped, unmappedTotal);

  rows.push({
    key: "fr-total-outflow",
    label: "Total Operating Outflows",
    isTotal: true,
    values: totalOutflowWithUnmapped,
  });

  rows.push({
    key: "fr-net-operating",
    label: "Net Operating Cash Flow",
    isTotal: true,
    values: netOperating,
  });

  if (closing) {
    rows.push({
      ...closing,
      key: "fr-closing",
      label: "Cash Closing",
    });
  }

  return { rows, unmappedRows };
}
